import {
  CreateSample,
  EngagementId,
  FileId,
  Paginated,
  PaginationArgs,
  PopulationId,
  Sample,
  SampleId,
  SampleItem,
  SampleMethod,
  SampleMin,
} from "platformed-client/types";
import {HTTPError} from "platformed-client";
import {invalidateQueries, q} from "../../state";
import {platformedClient} from "..";

/**
 * Mints a draft sample. The population is optional and, when supplied, only
 * attached: nothing is materialized and no population is locked until
 * {@link draw}.
 */
export async function create(engagementId: EngagementId, body: CreateSample): Promise<Sample> {
  const sample = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/samples",
    {path: {engagement_id: engagementId}, body},
  );
  // "Linked samples" counts drafts, so an attached population goes stale even
  // though a draft locks nothing.
  await invalidateQueries([
    q.tprm.engagementSamples.filter(engagementId),
    ...(sample.population
      ? [
          q.tprm.population.filter(sample.population.population_id),
          q.tprm.populationSamples.filter(sample.population.population_id),
        ]
      : []),
    q.tprm.engagementPopulations.filter(),
  ]);
  return sample;
}

/**
 * Points a sample at a population, for the first time or as a re-point. A
 * committed sample loses its items and returns to draft, since they were
 * pointers into the old population's rows. Rejected (409) while the sample is
 * locked.
 */
export async function updatePopulation(sampleId: SampleId, populationId: PopulationId): Promise<void> {
  await platformedClient.internalRequest("post", "/region/{region}/api/tprm/samples/{sample_id}/population", {
    path: {sample_id: sampleId},
    body: {population_id: populationId},
  });
  // The population it left changes its linked-sample count too, and is not named
  // here, so the population queries are invalidated broadly.
  await invalidateQueries([
    q.tprm.sample.filter(sampleId),
    q.tprm.sampleItems.filter(sampleId),
    q.tprm.engagementSamples.filter(),
    q.tprm.populationSamples.filter(),
    q.tprm.engagementPopulations.filter(),
    q.tprm.population.filter(),
  ]);
}

/**
 * Chooses a draft sample's method and, for a random one, the size it asks for.
 * This is the design; the seed the draw runs under lives with the draw and is
 * not expressible here. Rejected (409) once the sample has been drawn.
 */
export async function updateMethod(sampleId: SampleId, method: SampleMethod): Promise<void> {
  await platformedClient.internalRequest("post", "/region/{region}/api/tprm/samples/{sample_id}/method", {
    path: {sample_id: sampleId},
    body: {method},
  });
  // The method is denormalised into both sample listings as `SampleMin.method`.
  await invalidateQueries([
    q.tprm.sample.filter(sampleId),
    q.tprm.engagementSamples.filter(),
    q.tprm.populationSamples.filter(),
  ]);
}

/**
 * The sample's commit verb: materializes its items from its population and
 * locks that population. Rejected (409) when no population is attached, when
 * the population has not finished extracting, and on a second draw.
 */
export async function draw(sampleId: SampleId): Promise<Sample> {
  const sample = await platformedClient.internalRequest("post", "/region/{region}/api/tprm/samples/{sample_id}/draw", {
    path: {sample_id: sampleId},
  });
  // The sample flips to Sampling and its population becomes locked. A test
  // pointed at the sample carries it as `Test.sample`, so its page reads the
  // drawing — and subscribes to it — only once the test is invalidated too.
  await invalidateQueries([
    q.tprm.sample.filter(sampleId),
    q.tprm.sampleItems.filter(sampleId),
    q.tprm.test.filter(),
    q.tprm.engagementSamples.filter(),
    q.tprm.populationSamples.filter(),
    q.tprm.engagementPopulations.filter(),
    q.tprm.population.filter(),
  ]);
  return sample;
}

export async function updateName(sampleId: SampleId, name: string): Promise<void> {
  await platformedClient.internalRequest("post", "/region/{region}/api/tprm/samples/{sample_id}/name", {
    path: {sample_id: sampleId},
    body: {name},
  });
  // A sample's name is denormalised into every test drawn from it as
  // `TestMin.sample_name`, so the tests lists carry a stale copy until they are
  // invalidated too.
  await invalidateQueries([
    q.tprm.engagementSamples.filter(),
    q.tprm.populationSamples.filter(),
    q.tprm.sample.filter(sampleId),
    q.tprm.engagementTests.filter(),
    q.tprm.sampleTests.filter(),
  ]);
}

/** `null` clears the description; the column holds no empty string. */
export async function updateDescription(sampleId: SampleId, description: string | null): Promise<void> {
  await platformedClient.internalRequest("post", "/region/{region}/api/tprm/samples/{sample_id}/description", {
    path: {sample_id: sampleId},
    body: {description},
  });
  await invalidateQueries([
    q.tprm.engagementSamples.filter(),
    q.tprm.populationSamples.filter(),
    q.tprm.sample.filter(sampleId),
  ]);
}

/**
 * Locking freezes a sample's name, description and items. The first test to
 * freeze a copy of them takes the lock automatically, and taking it again
 * coalesces, so locking a locked sample changes nothing.
 */
export async function lock(sampleId: SampleId): Promise<Sample> {
  const sample = await platformedClient.internalRequest("post", "/region/{region}/api/tprm/samples/{sample_id}/lock", {
    path: {sample_id: sampleId},
  });
  await invalidateLock(sampleId);
  return sample;
}

/** The lock's only release. The tests that froze a copy of the sample are left as they are. */
export async function unlock(sampleId: SampleId): Promise<Sample> {
  const sample = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/samples/{sample_id}/unlock",
    {path: {sample_id: sampleId}},
  );
  await invalidateLock(sampleId);
  return sample;
}

// A row's rename and delete are gated on the lock, so the lists go as stale as
// the sample itself.
async function invalidateLock(sampleId: SampleId): Promise<void> {
  await invalidateQueries([
    q.tprm.sample.filter(sampleId),
    q.tprm.engagementSamples.filter(),
    q.tprm.populationSamples.filter(),
  ]);
}

export async function get(sampleId: SampleId): Promise<Sample> {
  return await platformedClient.internalRequest("get", "/region/{region}/api/tprm/samples/{sample_id}", {
    path: {sample_id: sampleId},
  });
}

export async function list(engagementId: EngagementId): Promise<SampleMin[]> {
  return await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/engagements/{engagement_id}/samples",
    {path: {engagement_id: engagementId}},
  );
}

export async function listForPopulation(populationId: PopulationId): Promise<SampleMin[]> {
  return await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/populations/{population_id}/samples",
    {path: {population_id: populationId}},
  );
}

export async function listItems(sampleId: SampleId, query: PaginationArgs): Promise<Paginated<SampleItem>> {
  return await platformedClient.internalRequest("get", "/region/{region}/api/tprm/samples/{sample_id}/items", {
    path: {sample_id: sampleId},
    query,
  });
}

/**
 * Writes a sample's drawn items to a CSV file and returns its id. The file is
 * a point-in-time snapshot, not a durable view kept in sync with the sample,
 * so nothing here needs invalidating. Rejected (409) until the sample has
 * finished being drawn.
 */
export async function exportCsv(sampleId: SampleId): Promise<FileId> {
  return await platformedClient.internalRequest("post", "/region/{region}/api/tprm/samples/{sample_id}/export", {
    path: {sample_id: sampleId},
  });
}

export async function delete_(sampleId: SampleId): Promise<void> {
  try {
    await platformedClient.internalRequest("delete", "/region/{region}/api/tprm/samples/{sample_id}", {
      path: {sample_id: sampleId},
    });
  } catch (e) {
    // The 409 is the in-use refusal. Delete is disabled while the cached test
    // count is nonzero, so a refusal means that count, and the linked-tests
    // list beside it, were stale.
    if (e instanceof HTTPError && e.response.status === 409) {
      await Promise.all([invalidateLock(sampleId), invalidateQueries([q.tprm.sampleTests.filter(sampleId)])]);
    }
    throw e;
  }
  // Invalidate the lists and the populations' linked-sample counts. We don't know
  // the population id here, so invalidate the population queries broadly (none
  // are deleted, so they refetch safely). Never invalidate the deleted sample's
  // own `sample`/`sampleItems` queries — those would 404 on the now-deleted id;
  // let them be GC'd.
  await invalidateQueries([
    q.tprm.engagementSamples.filter(),
    q.tprm.populationSamples.filter(),
    q.tprm.engagementPopulations.filter(),
    q.tprm.population.filter(),
  ]);
}
